import Alert from '@mui/material/Alert';
import { useAtom } from 'jotai';
import { useFormContext, useWatch } from 'react-hook-form';

import { SOC_PHONE_NUMBER } from '@/constants/common';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { ticketDialogIsOpenAtom, ticketIsEditingAtom } from '@/pages/Ticket';
import type { TicketDialog } from '@/pages/Ticket/Ticket.types';

import {
  CATEGORY_ID,
  SUBCATEGORY_ID
} from '../TicketCreateDialog/TicketCreateDialog.constants';

const ServiceFormAlerts = () => {
  const [{ id: selectedOrgId }] = useAtom(selectedOrganizationAtom);
  const [ticketIsEditing] = useAtom(ticketIsEditingAtom);
  const [ticketDialogIsOpen] = useAtom(ticketDialogIsOpenAtom);
  const {
    control,
    formState: { errors }
  } = useFormContext<TicketDialog>();

  const [ticketCategory, ticketSubcategory, ticketOrganizationId] = useWatch({
    control,
    name: ['ticket_category', 'ticket_subcategory', 'organization_id']
  });

  return (
    <>
      {/* display notification if organization "All Organizations" is selected */}
      {selectedOrgId === 0 && !ticketIsEditing && !ticketDialogIsOpen && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please select an organization from the topbar.
        </Alert>
      )}
      {/* display notification if organization in create dialog is different from currently selected org in app */}
      {selectedOrgId !== ticketOrganizationId && ticketDialogIsOpen && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You are in the process of creating a ticket for a different
          organization than the one currently selected.
        </Alert>
      )}
      {/* display notification if a watcher from the org has not been selected */}
      {errors.ticket_watchers && errors.ticket_watchers.type === 'validate' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Ticket must have at least one watcher from the organization
        </Alert>
      )}

      {/* category specific alerts ---------------------------------------- */}
      {/* display notification if category "Change Request" is selected, sub-category "Travel Notification" selected, and locations field has an error */}
      {ticketCategory === CATEGORY_ID.CHANGE_REQUEST &&
        ticketSubcategory === SUBCATEGORY_ID.TRAVEL_NOTIFICATION &&
        !!errors.custom_data?.travel_notification?.locations && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Ticket must have at least one travel location
          </Alert>
        )}
      {/* display notification if category "Escalation Request" is selected */}
      {ticketCategory === CATEGORY_ID.URGENT && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Typical SOC response times to Urgent tickets are under 1 hour, if you
          need to speak with an analyst immediately, call the SOC at{' '}
          <a href={`tel:${SOC_PHONE_NUMBER}`}>{SOC_PHONE_NUMBER}</a> after
          creating ticket.
        </Alert>
      )}
      {/* display notification if category "Vulnerability Management Services" is selected */}
      {ticketCategory === CATEGORY_ID.VULNERABILITY_MANAGEMENT && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This category is for customers with VMP or VMPE services and is
          managed by the VMP service delivery team. These tickets are not
          monitored by the SOC 24/7.
        </Alert>
      )}
    </>
  );
};

export default ServiceFormAlerts;
